import { BatchPhotoCaptureConfig, BatchPhotoUploadResult, PhotoUploadProgress, PhotoUploadResult } from '@services/camera/CameraTypes';

/**
 * What a {@link createBatchCaptureTestDouble} fake should report.
 *
 * Nothing is fabricated for you: an option you leave out is empty, so the bare factory resolves an
 * empty outcome and emits no progress. Script the outcome you actually want to exercise.
 */
export interface BatchCaptureTestDoubleOptions {
    /**
     * Per-photo results to report as successful uploads, in capture order. Defaults to none.
     */
    results?: PhotoUploadResult[];

    /**
     * Capture-order positions to report as failures. Defaults to none. Keep these within
     * `[0, results.length + failedIndices.length)` — the fake derives the batch size from the two
     * lengths together, so an index outside that range is simply never reached.
     */
    failedIndices?: number[];
}

/**
 * A stand-in with the same signature as `CameraCaptureService.captureImagesAndUpload`.
 */
export type BatchCaptureFake = (config: BatchPhotoCaptureConfig) => Promise<BatchPhotoUploadResult>;

/**
 * Builds a fake batch capture-and-upload call that consumers can inject in their own unit tests.
 *
 * CI has no camera and no host frame, so the real call cannot run there at all. The fake reproduces
 * the three behaviours a caller most needs to get right:
 *
 * 1. Progress arrives once per photo, in capture order, before the promise settles.
 * 2. Partial failure RESOLVES — failures appear as `failedIndices`, never as a rejection.
 * 3. A progress listener that throws does not disturb the session.
 *
 * This is a testing aid, not a runtime fallback. It never talks to a camera or an upload service,
 * and nothing in the library reaches for it — a production path that cannot serve a batch rejects
 * rather than inventing a result.
 *
 * @param options - the outcome the fake should report. See {@link BatchCaptureTestDoubleOptions}.
 * @returns a function matching `CameraCaptureService.captureImagesAndUpload`.
 */
export function createBatchCaptureTestDouble(options: BatchCaptureTestDoubleOptions = {}): BatchCaptureFake {
    const results = options.results || [];
    const failedIndices = options.failedIndices || [];
    const totalPhotos = results.length + failedIndices.length;

    return (config: BatchPhotoCaptureConfig): Promise<BatchPhotoUploadResult> => {
        for (let photoIndex = 0; photoIndex < totalPhotos; photoIndex++) {
            notifyPhotoUploaded(config, {
                photoIndex,
                totalPhotos,
                status: failedIndices.indexOf(photoIndex) === -1 ? 'uploaded' : 'failed',
            });
        }
        return Promise.resolve({ results, failedIndices });
    };
}

/**
 * Delivers one progress event, absorbing anything the listener throws.
 *
 * The real implementation must guard the same way: a caller's bad listener cannot be allowed to
 * abandon photos that have already been captured and uploaded.
 *
 * @param config - the session config, which may or may not carry a listener.
 * @param progress - the event to deliver.
 */
function notifyPhotoUploaded(config: BatchPhotoCaptureConfig, progress: PhotoUploadProgress): void {
    if (!config.onPhotoUploaded) {
        return;
    }
    try {
        config.onPhotoUploaded(progress);
    } catch (error) {
        console.error(`onPhotoUploaded listener threw for photo ${progress.photoIndex}; continuing`, error);
    }
}
